import { useEffect, useMemo, useState } from "react";
import { useTranslation } from "react-i18next";
import { keepPreviousData, useQuery } from "@tanstack/react-query";
import { Search } from "lucide-react";
import { listUsers } from "@/api/users";
import { getFeedback } from "@/api/feedback";
import { useAuthStore } from "@/store/auth";
import { Pagination } from "@/components/ui/Pagination";
import { OrderDetailModal } from "@/features/orders/components/OrderDetailModal";
import { AnalyticsFilterBar } from "@/features/reports/components/AnalyticsFilterBar";
import type { AnalyticsFilterValues } from "@/features/reports/components/AnalyticsFilterBar";
import { FeedbackList } from "@/features/feedback/components/FeedbackList";
import {
  analyticsDefaultDateFrom,
  analyticsDefaultDateTo,
} from "@/features/reports/analyticsFilters";
import { useTenantTimezone } from "@/features/settings/hooks/useTenantTimezone";
import type { ReviewsValues } from "@/features/reports/analyticsFilters";
import type { FeedbackParams } from "@/types/feedback";

const PAGE_SIZE = 20;
const RATINGS = ["5", "4", "3", "2", "1"];
const SEARCH_DEBOUNCE_MS = 350;

interface Props {
  /** Current resolved values for the reviews filter params (URL ∪ defaults). */
  values: ReviewsValues;
  /** Merge a patch of reviews filter params into the shared URL state. */
  setValues: (patch: Partial<ReviewsValues>) => void;
}

/**
 * Reviews tab body — recipient feedback list with rating/comment/search/date
 * filters and pagination. Extracted from the former `FeedbackPage` (Sprint 117,
 * TASK-3828) so it can live inside the consolidated `/analytics` page.
 *
 * Sprint 118 (TASK-3841): the filter UI was unified onto the shared
 * {@link AnalyticsFilterBar}. The bespoke `FeedbackFilterBar` (with its own
 * Apply button) was retired so every analytics tab uses the same date-range +
 * preset bar; the rating/search/"with comments" controls now ride in the bar's
 * `rightSlot`. Apply model: **live-apply** (matches the rest of the section) —
 * rating/checkbox/date/preset commit immediately and the comment search is
 * debounced ({@link SEARCH_DEBOUNCE_MS}ms) instead of waiting for an Apply click.
 */
export function ReviewsTab({ values, setValues }: Props) {
  const { t } = useTranslation();
  const tenantTz = useTenantTimezone();
  const [selectedOrderId, setSelectedOrderId] = useState<number | null>(null);

  // Driver list requires company_admin+; dispatcher cannot call GET /users/ list.
  const canListUsers = useAuthStore((s) => s.user?.role !== "dispatcher");

  // Driver options for the filter — same source as the shared analytics filter.
  const { data: driversData } = useQuery({
    queryKey: ["users", "drivers"],
    queryFn: () => listUsers({ role: "driver", page_size: 200 }),
    enabled: canListUsers,
  });
  const driverOptions = (driversData?.results ?? []).map((u) => ({
    id: u.id,
    name: `${u.first_name} ${u.last_name}`.trim() || u.email,
  }));

  // Local draft for the comment search; committed to the URL after a debounce so
  // typing stays responsive without the explicit Apply button.
  const [searchDraft, setSearchDraft] = useState(values.search);

  // Re-sync the draft when the committed value changes externally (reset / URL).
  useEffect(() => {
    setSearchDraft(values.search);
  }, [values.search]);

  useEffect(() => {
    if (searchDraft === values.search) return;
    const id = window.setTimeout(() => {
      setValues({ search: searchDraft, page: "1" });
    }, SEARCH_DEBOUNCE_MS);
    return () => window.clearTimeout(id);
    // `setValues` is stable; intentionally exclude it and `values.search` to
    // debounce purely on the draft.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [searchDraft]);

  const page = Number(values.page) || 1;

  const queryParams = useMemo<FeedbackParams>(() => {
    const params: FeedbackParams = {
      date_from: values.date_from || undefined,
      date_to: values.date_to || undefined,
      search: values.search || undefined,
      page,
    };
    if (values.rating) params.rating = Number(values.rating);
    if (values.driver_id) params.driver_id = Number(values.driver_id);
    if (values.has_comment === "true") params.has_comment = true;
    return params;
  }, [
    values.date_from,
    values.date_to,
    values.search,
    values.rating,
    values.driver_id,
    values.has_comment,
    page,
  ]);

  const { data, isLoading, isFetching } = useQuery({
    queryKey: ["feedback", queryParams],
    queryFn: () => getFeedback(queryParams),
    placeholderData: keepPreviousData,
  });

  /* Any non-default filter (besides paging) means an empty result is "filtered".
     TASK-5041: the two date bounds are compared against the TENANT's current
     default window, resolved per render. They used to be module constants
     frozen at import, so after a midnight rollover the page's own default
     window no longer equalled them and every empty day read as «filtered». */
  const filtered =
    values.rating !== "" ||
    values.driver_id !== "" ||
    values.has_comment === "true" ||
    values.search !== "" ||
    values.date_from !== analyticsDefaultDateFrom(tenantTz) ||
    values.date_to !== analyticsDefaultDateTo(tenantTz);

  // Shared bar values — Reviews has no vehicle filter, so vehicle_id is empty.
  const barValues: AnalyticsFilterValues = {
    date_from: values.date_from,
    date_to: values.date_to,
    vehicle_id: "",
    driver_id: values.driver_id,
    preset: values.preset,
  };

  // Changing any shared-bar filter (date/driver/preset) resets the page to 1.
  function handleBarChange(patch: Partial<AnalyticsFilterValues>) {
    const next: Partial<ReviewsValues> = { page: "1" };
    if (patch.date_from !== undefined) next.date_from = patch.date_from;
    if (patch.date_to !== undefined) next.date_to = patch.date_to;
    if (patch.driver_id !== undefined) next.driver_id = patch.driver_id;
    if (patch.preset !== undefined) next.preset = patch.preset;
    setValues(next);
  }

  const rows = data?.results ?? [];
  const total = data?.count ?? 0;
  const hasPrevious = Boolean(data?.previous);
  const hasNext = Boolean(data?.next);
  const showingLabel =
    total > 0
      ? t("feedback.pagination.showing", {
          from: (page - 1) * PAGE_SIZE + 1,
          to: Math.min(page * PAGE_SIZE, total),
          total,
        })
      : "";

  return (
    <div className="space-y-4">
      {/* Filters — shared bar + Reviews-specific controls in the right slot */}
      <AnalyticsFilterBar
        values={barValues}
        vehicleOptions={[]}
        driverOptions={driverOptions}
        onChange={handleBarChange}
        hideVehicle
        groupRightSlot
        tenantTz={tenantTz}
        rightSlot={
          <div className="flex flex-wrap items-end gap-3">
            {/* Rating */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t("feedback.filter.rating")}
              </label>
              <select
                value={values.rating}
                onChange={(e) =>
                  setValues({ rating: e.target.value, page: "1" })
                }
                aria-label={t("feedback.filter.rating")}
                className="h-[30px] rounded-lg border border-gray-200 bg-gray-50 px-2.5 text-xs text-gray-900 outline-none focus:border-brand-primary focus:ring-1 focus:ring-brand-primary"
              >
                <option value="">{t("feedback.filter.allRatings")}</option>
                {RATINGS.map((r) => (
                  <option key={r} value={r}>
                    {r}
                  </option>
                ))}
              </select>
            </div>

            {/* Comment search (debounced live-apply) */}
            <div className="flex flex-col gap-1">
              <label className="text-[10px] font-semibold uppercase tracking-wider text-gray-400">
                {t("feedback.filter.search")}
              </label>
              <div className="flex items-center gap-1.5 rounded-lg border border-gray-200 bg-gray-50 px-2.5 py-1.5">
                <Search size={13} className="shrink-0 text-gray-400" />
                <input
                  type="search"
                  value={searchDraft}
                  onChange={(e) => setSearchDraft(e.target.value)}
                  placeholder={t("feedback.filter.searchPlaceholder")}
                  aria-label={t("feedback.filter.search")}
                  className="w-44 bg-transparent text-xs text-gray-900 outline-none"
                />
              </div>
            </div>

            {/* With comments only */}
            <label className="flex h-[30px] cursor-pointer select-none items-center gap-2 text-xs font-medium text-gray-600">
              <input
                type="checkbox"
                checked={values.has_comment === "true"}
                onChange={(e) =>
                  setValues({
                    has_comment: e.target.checked ? "true" : "",
                    page: "1",
                  })
                }
                className="h-3.5 w-3.5 rounded border-gray-300 accent-brand-primary focus:ring-brand-primary"
              />
              {t("feedback.filter.withCommentsOnly")}
            </label>
          </div>
        }
      />

      {/* List */}
      <FeedbackList
        rows={rows}
        isLoading={isLoading}
        filtered={filtered}
        onOpenOrder={setSelectedOrderId}
        tenantTz={tenantTz}
      />

      {/* Pagination */}
      {!isLoading && (hasPrevious || hasNext) && (
        <Pagination
          label={showingLabel}
          hasPrevious={hasPrevious && !isFetching}
          hasNext={hasNext && !isFetching}
          onPrevious={() => setValues({ page: String(page - 1) })}
          onNext={() => setValues({ page: String(page + 1) })}
        />
      )}

      {/* Order detail modal */}
      {selectedOrderId !== null && (
        <OrderDetailModal
          open={selectedOrderId !== null}
          onOpenChange={(o) => {
            if (!o) setSelectedOrderId(null);
          }}
          orderId={selectedOrderId}
        />
      )}
    </div>
  );
}
